import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import Typography from '@precis-digital/kurama/Typography';
import { type ReactElement } from 'react';
import { ToolCallDetailChip, ToolCallDetailChipRow } from 'shared/copilot/components/ToolCall/ToolCallDetailChip';
import { OPERATOR_OPTIONS } from 'shared/constants/filterOperators';
import { type CopilotActionDisplayResolvers } from 'shared/copilot/toolRegistry/types';
import { TOOL_DETAIL_CHIP_FIELDS } from 'shared/copilot/toolDetailFieldManifest';
interface SetPageFilterArguments {
  filters?: Array<{
    dimensionId?: string;
    operatorId?: string;
    values?: string[];
    value?: string;
  }>;
}
interface UpdateDateRangeArguments {
  startDate?: string;
  endDate?: string;
}
interface UpdateListingFiltersArguments {
  filters?: Array<{
    accessor?: string;
    values?: string[];
  }>;
}
const FILTER_DIMENSION_LABELS: Record<string, string> = {
  market_name: 'Market',
  channel_grouping: 'Channel',
  market_channel_grouping: 'Market',
  custom_channel_grouping: 'Channel'
};

/**
 * Coerces a single filter entry from tool args into a non-empty trimmed string.
 * Skips null/undefined/empty and unsupported object shapes.
 */
const coerceFilterValueToString = (entry: unknown): string | undefined => {
  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof entry === 'number' || typeof entry === 'boolean') {
    return String(entry);
  }
  if (entry != null && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    const candidate = record.value ?? record.id ?? record.label;
    return coerceFilterValueToString(candidate);
  }
  return undefined;
};

/**
 * Normalizes filter `values` or a single `value` from tool call args into a string array.
 * Accepts common agent shapes: `values: ["META"]`, `value: "META"`, `values: "META"`,
 * and non-string entries (numbers / `{ value }` / `{ id }` objects). Never throws.
 */
export const getToolFilterValues = (filter: {
  dimensionId?: string;
  operatorId?: string;
  values?: unknown;
  value?: unknown;
}): string[] => {
  if (Array.isArray(filter.values)) {
    return filter.values.map(coerceFilterValueToString).filter((value): value is string => value != null);
  }
  if (typeof filter.values === 'string') {
    const fromValuesString = coerceFilterValueToString(filter.values);
    if (fromValuesString != null) {
      return [fromValuesString];
    }
  }
  const singular = coerceFilterValueToString(filter.value);
  return singular == null ? [] : [singular];
};
export const FilterChipsPreview = t0 => {
  const $ = _c(5);
  const {
    args
  } = t0;
  if (args.filters == null || args.filters.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Grid display="flex" width="fit-content"><Typography variant="body3">No filters</Typography></Grid>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== args.filters) {
    t1 = args.filters.map(_temp);
    $[1] = args.filters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <ToolCallDetailChipRow>{t1}</ToolCallDetailChipRow>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ListingSearchPreview = t0 => {
  const $ = _c(4);
  const {
    args
  } = t0;
  const query = args.query ?? "";
  let t1;
  if ($[0] !== query) {
    t1 = query.length > 0 ? <>Search: <strong>{query}</strong></> : "Clear search";
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ToolCallDetailChipRow><ToolCallDetailChip label={t1} /></ToolCallDetailChipRow>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const ListingFilterChipsPreview = t0 => {
  const $ = _c(5);
  const {
    args
  } = t0;
  if (args.filters == null || args.filters.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Grid display="flex" width="fit-content"><Typography variant="body3">No filters</Typography></Grid>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== args.filters) {
    t1 = args.filters.map(_temp2);
    $[1] = args.filters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <ToolCallDetailChipRow>{t1}</ToolCallDetailChipRow>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DateRangePreview = t0 => {
  const $ = _c(2);
  const {
    args
  } = t0;
  const label = args.startDate != null && args.endDate != null ? `${args.startDate} - ${args.endDate}` : "Date range";
  let t1;
  if ($[0] !== label) {
    t1 = <ToolCallDetailChip label={label} />;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ScorecardMetricsPreview = t0 => {
  const $ = _c(7);
  const {
    metrics,
    getMetricLabel
  } = t0;
  let t1;
  if ($[0] !== getMetricLabel || $[1] !== metrics) {
    let t2;
    if ($[3] !== getMetricLabel) {
      t2 = metric => <ToolCallDetailChip key={metric} label={getMetricLabel?.(metric) ?? metric} />;
      $[3] = getMetricLabel;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = metrics.map(t2);
    $[0] = getMetricLabel;
    $[1] = metrics;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <ToolCallDetailChipRow>{t1}</ToolCallDetailChipRow>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
interface SettingChipItem {
  key: string;
  label: string;
  value: string;
}
const SettingChipsPreview = t0 => {
  const $ = _c(5);
  const {
    items
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Grid display="flex" width="fit-content"><Typography variant="body3">—</Typography></Grid>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== items) {
    t1 = items.map(_temp3);
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <ToolCallDetailChipRow>{t1}</ToolCallDetailChipRow>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * Reads a string array argument from parsed tool call args.
 * Filters out non-string entries so chip previews only show valid values.
 */
const getStringArrayArg = (args: Record<string, unknown>, key: string): string[] | undefined => {
  const value = args[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : undefined;
};

/**
 * Reads a string argument from parsed tool call args when present.
 * Returns undefined for missing or non-string values.
 */
const getStringArg = (args: Record<string, unknown>, key: string): string | undefined => {
  const value = args[key];
  return typeof value === 'string' ? value : undefined;
};

/**
 * Formats a scalar tool arg for chip display (string, number, or boolean).
 * Returns undefined for missing or unsupported types.
 */
const formatScalarArg = (args: Record<string, unknown>, key: string): string | undefined => {
  const value = args[key];
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
};
const formatDateRange = (args: Record<string, unknown>): string | undefined => {
  const startDate = getStringArg(args, 'start_date') ?? getStringArg(args, 'startDate');
  const endDate = getStringArg(args, 'end_date') ?? getStringArg(args, 'endDate');
  if (startDate != null && endDate != null) {
    return `${startDate} - ${endDate}`;
  }
  return undefined;
};
const formatStringList = (args: Record<string, unknown>, key: string): string | undefined => {
  const values = getStringArrayArg(args, key);
  return values != null && values.length > 0 ? values.join(', ') : undefined;
};
const buildSettingChipsFromArgs = (args: Record<string, unknown>, fields: Array<{
  key: string;
  label: string;
}>): React.ReactNode => {
  const items: SettingChipItem[] = fields.map(({
    key,
    label
  }) => {
    const scalarValue = formatScalarArg(args, key);
    if (scalarValue != null) {
      return {
        key,
        label,
        value: scalarValue
      };
    }
    const listValue = formatStringList(args, key);
    if (listValue != null) {
      return {
        key,
        label,
        value: listValue
      };
    }
    return null;
  }).filter((item): item is SettingChipItem => item != null);
  const dateRange = formatDateRange(args);
  if (dateRange != null) {
    items.unshift({
      key: 'date-range',
      label: 'date range',
      value: dateRange
    });
  }
  return <SettingChipsPreview items={items} />;
};

/**
 * Joins a list for display or returns `none` when empty.
 * Used in action detail chips for optional breakdown selections.
 */
const formatListOrNone = (values: string[]): string => values.length > 0 ? values.join(', ') : 'none';

/**
 * Builds action detail content for progress-over-time chart setting changes.
 * Renders metrics, breakdowns, and aggregation as labeled chips.
 */
const getProgressOverTimeChartDetailContent = (args: Record<string, unknown>, resolvers: CopilotActionDisplayResolvers): React.ReactNode => {
  const items: SettingChipItem[] = [];
  const metrics = getStringArrayArg(args, 'metrics');
  if (metrics != null && metrics.length > 0) {
    items.push({
      key: 'metrics',
      label: 'metrics',
      value: metrics.map(metric => resolvers.getMetricLabel?.(metric) ?? metric).join(', ')
    });
  }
  const breakdownMarkets = getStringArrayArg(args, 'breakdown_markets');
  if (breakdownMarkets != null) {
    items.push({
      key: 'markets',
      label: 'markets',
      value: formatListOrNone(breakdownMarkets)
    });
  }
  const breakdownChannels = getStringArrayArg(args, 'breakdown_channels');
  if (breakdownChannels != null) {
    items.push({
      key: 'channels',
      label: 'channels',
      value: formatListOrNone(breakdownChannels)
    });
  }
  const aggregation = getStringArg(args, 'aggregation');
  if (aggregation != null) {
    items.push({
      key: 'aggregation',
      label: 'aggregation',
      value: aggregation
    });
  }
  return <SettingChipsPreview items={items} />;
};

/**
 * Builds action detail content for detailed table column setting changes.
 * Renders selected dimensions and metrics as labeled chips.
 */
const getDetailedTableDetailContent = (args: Record<string, unknown>, resolvers: CopilotActionDisplayResolvers): React.ReactNode => {
  const items: SettingChipItem[] = [];
  const dimensions = getStringArrayArg(args, 'dimensions');
  if (dimensions != null) {
    items.push({
      key: 'dimensions',
      label: 'dimensions',
      value: formatListOrNone(dimensions)
    });
  }
  const metrics = getStringArrayArg(args, 'metrics');
  if (metrics != null) {
    items.push({
      key: 'metrics',
      label: 'metrics',
      value: metrics.length > 0 ? metrics.map(metric => resolvers.getMetricLabel?.(metric) ?? metric).join(', ') : 'none'
    });
  }
  return <SettingChipsPreview items={items} />;
};

/**
 * Builds action detail content for conversion event selection.
 * Shows the event name and optional source in a single chip.
 */
const getSelectConversionEventDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const eventName = getStringArg(args, 'eventName') ?? 'Conversion event';
  const eventSource = getStringArg(args, 'eventSource');
  const sourceSuffix = eventSource == null ? '' : ` from ${eventSource}`;
  const label = `${eventName}${sourceSuffix}`;
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={label} />
    </ToolCallDetailChipRow>;
};

/**
 * Builds action detail content for date range updates.
 * Delegates to the shared date range chip preview component.
 */
const getUpdateDateRangeDetailContent = (args: Record<string, unknown>): React.ReactNode => <DateRangePreview args={{
  startDate: getStringArg(args, 'startDate'),
  endDate: getStringArg(args, 'endDate')
}} />;
type SetPageFilterEntry = NonNullable<SetPageFilterArguments['filters']>[number];

/**
 * Type guard for a single filter entry in `set_page_filters` tool args.
 * Ensures only object-shaped filters are passed to the chip preview.
 */
const isSetPageFilterEntry = (entry: unknown): entry is SetPageFilterEntry => typeof entry === 'object' && entry != null;

/**
 * Builds action detail content for homepage filter changes.
 * Parses filter entries from tool args and renders them as chips.
 */
const getSetPageFiltersDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const filters = args.filters;
  const parsedFilters = Array.isArray(filters) ? filters.filter(isSetPageFilterEntry) : undefined;
  return <FilterChipsPreview args={{
    filters: parsedFilters
  }} />;
};

/**
 * Builds action detail content for scorecard metric selection changes.
 * Uses page-specific metric label resolvers when available.
 */
const getSelectMetricsDetailContent = (args: Record<string, unknown>, resolvers: CopilotActionDisplayResolvers): React.ReactNode => {
  const metrics = getStringArrayArg(args, 'metrics') ?? [];
  return <ScorecardMetricsPreview metrics={metrics} getMetricLabel={resolvers.getMetricLabel} />;
};

/**
 * Builds action detail content for budget optimiser ad spend target updates.
 * Shows the target value in a single chip.
 */
const getBudgetOptimiserTargetDetailContent = (args: Record<string, unknown>): React.ReactNode => <ToolCallDetailChipRow>
    <ToolCallDetailChip label={getStringArg(args, 'adSpendTarget') ?? 'Unknown target'} />
  </ToolCallDetailChipRow>;
const getGoogleAdsQueryDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const query = getStringArg(args, 'query');
  if (query == null) {
    return buildSettingChipsFromArgs(args, [{
      key: 'customer_id',
      label: 'customer'
    }]);
  }
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={query} />
    </ToolCallDetailChipRow>;
};
const getTransformDataDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const query = getStringArg(args, 'query');
  if (query == null) {
    return <Typography variant="body3">—</Typography>;
  }
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={query} />
    </ToolCallDetailChipRow>;
};
const getCreateReportDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const title = getStringArg(args, 'title');
  const rowCount = Array.isArray(args.data) ? args.data.length : undefined;
  let label: string;
  if (title != null) {
    label = rowCount != null ? `${title} (${rowCount} rows)` : title;
  } else if (typeof args.row_count === 'number') {
    label = `${args.row_count} rows`;
  } else {
    label = 'Custom report';
  }
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={label} />
    </ToolCallDetailChipRow>;
};
const getRunSkillScriptDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const skillName = getStringArg(args, 'skill_name') ?? getStringArg(args, 'skillName') ?? 'Skill';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={skillName} />
    </ToolCallDetailChipRow>;
};
const LEADERBOARD_VIEW_LABELS: Record<string, string> = {
  top_performance: 'Top performance',
  scaling: 'Scaling',
  declining: 'Declining'
};
const getScalarChipDetailContent = (args: Record<string, unknown>, argKey: string): ReactElement => {
  const value = formatScalarArg(args, argKey);
  if (value == null || value.trim().length === 0) {
    return <ToolCallDetailChipRow>
        <ToolCallDetailChip label="none" />
      </ToolCallDetailChipRow>;
  }
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={value} />
    </ToolCallDetailChipRow>;
};
const getStringListChipDetailContent = (args: Record<string, unknown>, argKey: string): ReactElement => {
  const values = getStringArrayArg(args, argKey) ?? [];
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={values.length > 0 ? values.join(', ') : 'none'} />
    </ToolCallDetailChipRow>;
};
const getUpdateLeaderboardViewDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const view = getStringArg(args, 'view');
  const label = view == null ? 'Leaderboard view' : LEADERBOARD_VIEW_LABELS[view] ?? view;
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={label} />
    </ToolCallDetailChipRow>;
};
const getCreateDashboardDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const templateId = getStringArg(args, 'templateId') ?? 'Dashboard template';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={templateId.replaceAll('_', ' ')} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceTabDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const tab = getStringArg(args, 'tab') ?? 'tab';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={tab} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceRoleFilterDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const roles = Array.isArray(args.roles) ? args.roles.map(String) : [];
  const label = roles.length === 0 ? 'all roles' : roles.join(', ');
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={label} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceInviteDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const invites = Array.isArray(args.invites) ? args.invites : [];
  if (invites.length === 0) {
    return <Typography variant="body3">No invites</Typography>;
  }
  return <ToolCallDetailChipRow>
      {invites.map((invite, index) => {
      const record = invite as {
        email?: string;
        role?: string;
      };
      const email = record.email ?? 'unknown';
      const role = record.role ?? 'viewer';
      return <ToolCallDetailChip key={`${email}-${index}`} label={`${email} (${role})`} />;
    })}
    </ToolCallDetailChipRow>;
};
const getWorkspaceMemberRoleDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const member = getStringArg(args, 'memberIdOrEmail') ?? 'member';
  const role = getStringArg(args, 'role') ?? 'role';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={`${member} → ${role}`} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceMemberIdDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const member = getStringArg(args, 'memberIdOrEmail') ?? 'member';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={member} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceInviteIdDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const invite = getStringArg(args, 'inviteIdOrEmail') ?? 'invite';
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={invite} />
    </ToolCallDetailChipRow>;
};
const getWorkspaceNotificationSettingsDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const chips: string[] = [];
  if (typeof args.jobStatusesOn === 'boolean') chips.push(`job statuses: ${args.jobStatusesOn ? 'on' : 'off'}`);
  if (typeof args.recommendationsOn === 'boolean') chips.push(`recommendations: ${args.recommendationsOn ? 'on' : 'off'}`);
  if (typeof args.datasourcesOn === 'boolean') chips.push(`data sources: ${args.datasourcesOn ? 'on' : 'off'}`);
  if (Array.isArray(args.recommendationSeverities)) {
    chips.push(`severities: ${args.recommendationSeverities.map(String).join(', ')}`);
  }
  if (chips.length === 0) {
    return <Typography variant="body3">Notification settings</Typography>;
  }
  return <ToolCallDetailChipRow>
      {chips.map(chip => <ToolCallDetailChip key={chip} label={chip} />)}
    </ToolCallDetailChipRow>;
};
const getManifestChipDetailContent = (toolName: string, args: Record<string, unknown>): React.ReactNode | null => {
  const fields = TOOL_DETAIL_CHIP_FIELDS[toolName];
  if (fields == null) {
    return null;
  }
  return buildSettingChipsFromArgs(args, fields);
};
const getCreateProjectDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const title = getStringArg(args, 'title') ?? 'Project';
  const isInternal = args.isInternal === true;
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={title} />
      {isInternal ? <ToolCallDetailChip label="Private" /> : null}
    </ToolCallDetailChipRow>;
};
const getAddProjectResourceDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const title = getStringArg(args, 'title') ?? 'Resource';
  const url = getStringArg(args, 'url');
  return <ToolCallDetailChipRow>
      <ToolCallDetailChip label={title} />
      {url != null ? <ToolCallDetailChip label={url} /> : null}
    </ToolCallDetailChipRow>;
};
const getUpdateProjectDetailContent = (args: Record<string, unknown>): React.ReactNode => {
  const title = getStringArg(args, 'title');
  const description = getStringArg(args, 'description');
  const isInternal = args.isInternal === true ? 'Private' : args.isInternal === false ? 'Public' : null;
  const namingConvention = getStringArg(args, 'namingConvention');
  return <ToolCallDetailChipRow>
      {title != null ? <ToolCallDetailChip label={title} /> : null}
      {description != null ? <ToolCallDetailChip label={description} /> : null}
      {isInternal != null ? <ToolCallDetailChip label={isInternal} /> : null}
      {namingConvention != null ? <ToolCallDetailChip label={namingConvention} /> : null}
    </ToolCallDetailChipRow>;
};

/** Explicit ReactElement on arrows — React 19 `ReactNode` includes `Promise` and trips promise-function-async. */
const TOOL_DETAIL_CONTENT_BY_NAME: Record<string, (args: Record<string, unknown>, resolvers: CopilotActionDisplayResolvers) => React.ReactNode> = {
  update_date_range: getUpdateDateRangeDetailContent,
  set_page_filters: getSetPageFiltersDetailContent,
  update_listview_filters: (args): ReactElement => <ListingFilterChipsPreview args={args as UpdateListingFiltersArguments} />,
  update_listview_search: (args): ReactElement => <ListingSearchPreview args={args as {
    query?: string;
  }} />,
  select_metrics_in_scorecard_group: getSelectMetricsDetailContent,
  update_progress_over_time_chart_settings: getProgressOverTimeChartDetailContent,
  update_detailed_table_settings: getDetailedTableDetailContent,
  select_conversion_event: getSelectConversionEventDetailContent,
  update_budget_optimiser_ad_spend_target: getBudgetOptimiserTargetDetailContent,
  run_google_ads_query: getGoogleAdsQueryDetailContent,
  transform_data: getTransformDataDetailContent,
  create_report: getCreateReportDetailContent,
  create_project: getCreateProjectDetailContent,
  run_skill_script: getRunSkillScriptDetailContent,
  set_search: (args): ReactElement => getScalarChipDetailContent(args, 'search'),
  set_group_by: (args): ReactElement => getScalarChipDetailContent(args, 'groupBy'),
  update_leaderboard_view: getUpdateLeaderboardViewDetailContent,
  update_chart_type: (args): ReactElement => getScalarChipDetailContent(args, 'chartType'),
  update_chart_settings: (args): ReactElement => getStringListChipDetailContent(args, 'metrics'),
  update_table_settings: (args): ReactElement => getStringListChipDetailContent(args, 'metrics'),
  update_chart_ads: (args): ReactElement => getStringListChipDetailContent(args, 'ads'),
  update_ads_report_view: (args): ReactElement => getScalarChipDetailContent(args, 'view'),
  create_dashboard: getCreateDashboardDetailContent,
  select_creative_report: (args): ReactElement => getScalarChipDetailContent(args, 'reportId'),
  open_dashboard: (args): ReactElement => getScalarChipDetailContent(args, 'dashboardId'),
  switch_workspace_details_tab: getWorkspaceTabDetailContent,
  filter_workspace_members_by_role: getWorkspaceRoleFilterDetailContent,
  invite_workspace_members: getWorkspaceInviteDetailContent,
  update_workspace_member_role: getWorkspaceMemberRoleDetailContent,
  remove_workspace_member: getWorkspaceMemberIdDetailContent,
  resend_workspace_invite: getWorkspaceInviteIdDetailContent,
  revoke_workspace_invite: getWorkspaceInviteIdDetailContent,
  update_workspace_notification_settings: getWorkspaceNotificationSettingsDetailContent,
  add_project_resource: getAddProjectResourceDetailContent,
  update_project: getUpdateProjectDetailContent
};

/**
 * Routes a tool name to the appropriate action detail renderer for action cards.
 * Falls back to JSON stringification for tools without a dedicated preview.
 */
export const getActionDetailContent = (toolName: string, args: Record<string, unknown> | undefined, resolvers: CopilotActionDisplayResolvers = {}): React.ReactNode => {
  if (args == null) {
    return <Typography variant="body3">—</Typography>;
  }
  const renderDetail = TOOL_DETAIL_CONTENT_BY_NAME[toolName];
  if (renderDetail != null) {
    return renderDetail(args, resolvers);
  }
  const manifestDetail = getManifestChipDetailContent(toolName, args);
  if (manifestDetail != null) {
    return manifestDetail;
  }
  return <Typography variant="body3">{JSON.stringify(args)}</Typography>;
};
function _temp(filter, index) {
  const dimensionLabel = filter.dimensionId == null ? "Filter" : FILTER_DIMENSION_LABELS[filter.dimensionId] ?? filter.dimensionId;
  const operatorLabel = filter.operatorId == null ? "" : OPERATOR_OPTIONS[filter.operatorId]?.label.toLowerCase() ?? filter.operatorId;
  const valueLabel = getToolFilterValues({
    dimensionId: filter.dimensionId ?? "",
    operatorId: filter.operatorId ?? "",
    values: filter.values,
    value: filter.value
  }).join(", ");
  const operatorText = operatorLabel.length > 0 ? ` ${operatorLabel} ` : " is ";
  return <ToolCallDetailChip key={`${filter.dimensionId ?? "filter"}-${index}`} label={<>{dimensionLabel}{operatorText}<strong>{valueLabel}</strong></>} />;
}
function _temp2(filter, index) {
  const filterLabel = filter.accessor ?? "Filter";
  const valueLabel = (filter.values ?? []).join(", ");
  return <ToolCallDetailChip key={`${filter.accessor ?? "filter"}-${index}`} label={<>{filterLabel} is <strong>{valueLabel.length > 0 ? valueLabel : "\u2014"}</strong></>} />;
}
function _temp3(item) {
  return <ToolCallDetailChip key={item.key} label={<>{item.label}: <strong>{item.value}</strong></>} />;
}